import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDownRight } from "lucide-react";
import { MaskLine } from "./Reveal";
import { IMAGES, SOCIALS } from "../data/services";

const ease = [0.22, 1, 0.36, 1];

function FloatingCard({ src, className, style, delay, y }) {
  return (
    <motion.div
      style={{ ...style, y }}
      initial={{ opacity: 0, y: 60, rotate: style?.["--r"] || 0 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 1, delay, ease }}
      className={`absolute overflow-hidden rounded-md border border-white/10 shadow-2xl shadow-black/60 ${className}`}
    >
      <img src={src} alt="Graded football card" className="w-full h-full object-cover" />
    </motion.div>
  );
}

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const y1 = useTransform(scrollYProgress, [0, 1], ["0%", "-40%"]);
  const y2 = useTransform(scrollYProgress, [0, 1], ["0%", "-20%"]);
  const y3 = useTransform(scrollYProgress, [0, 1], ["0%", "-60%"]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "-15%"]);

  const scrollTo = (id) =>
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

  return (
    <section
      ref={ref}
      className="relative min-h-[100svh] overflow-hidden bg-[#0a0a0a] flex items-end"
      data-testid="hero"
    >
      {/* Gold glow + grid backdrop */}
      <div
        className="absolute top-0 right-0 h-[700px] w-[700px] rounded-full blur-[160px] opacity-25 -z-0"
        style={{ background: "radial-gradient(circle, #ebb323, transparent 70%)" }}
      />
      <div
        className="absolute inset-0 -z-0 opacity-[0.04]"
        style={{
          backgroundImage:
            "linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)",
          backgroundSize: "64px 64px",
        }}
      />

      {/* Floating real cards */}
      <div className="absolute inset-0 hidden md:block pointer-events-none">
        <FloatingCard
          src={IMAGES.cardYamalWonderkids}
          className="w-[210px] h-[294px] lg:w-[250px] lg:h-[350px]"
          style={{ top: "14%", right: "26%", rotate: "-9deg", "--r": -9, zIndex: 10 }}
          delay={0.5}
          y={y2}
        />
        <FloatingCard
          src={IMAGES.cardBellingham}
          className="w-[240px] h-[336px] lg:w-[290px] lg:h-[406px]"
          style={{ top: "8%", right: "6%", rotate: "7deg", "--r": 7, zIndex: 20 }}
          delay={0.35}
          y={y1}
        />
        <FloatingCard
          src={IMAGES.cardMessi}
          className="w-[180px] h-[252px] lg:w-[220px] lg:h-[308px]"
          style={{ top: "44%", right: "20%", rotate: "4deg", "--r": 4, zIndex: 30 }}
          delay={0.65}
          y={y3}
        />
      </div>

      {/* Bottom fade so text is legible */}
      <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-[#0a0a0a] via-[#0a0a0a]/70 to-transparent -z-0 pointer-events-none" />

      {/* Top overline */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.1, duration: 0.8 }}
        className="absolute top-24 left-5 sm:left-8 right-5 sm:right-8 flex justify-between font-mono text-[11px] uppercase tracking-[0.25em] text-zinc-400 z-30"
      >
        <span>{SOCIALS.location}</span>
        <span className="hidden sm:block">Grade · Consign · Broadcast</span>
      </motion.div>

      <motion.div
        style={{ y: textY }}
        className="relative z-10 max-w-[1400px] mx-auto w-full px-5 sm:px-8 pb-16 sm:pb-24"
      >
        <div className="font-mono text-xs uppercase tracking-[0.3em] text-gold mb-6 overflow-hidden">
          <motion.span
            className="block"
            initial={{ y: "110%" }}
            animate={{ y: "0%" }}
            transition={{ delay: 0.3, duration: 0.8, ease }}
          >
            Creator Hub × Premier Card Services
          </motion.span>
        </div>

        <h1 className="font-display font-semibold uppercase tracking-tighter leading-[0.82] text-[15vw] sm:text-[13vw] lg:text-[10.5vw]">
          <MaskLine delay={0.35}>South East</MaskLine>
          <MaskLine delay={0.5} className="text-gold">
            Collectibles
          </MaskLine>
        </h1>

        <div className="mt-10 flex flex-col lg:flex-row lg:items-end justify-between gap-8">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.8 }}
            className="max-w-xl text-zinc-300 text-base lg:text-lg leading-relaxed"
          >
            Two arms, one obsession with quality. <strong className="text-white">SEC Studios</strong> builds
            broadcast-ready creators. <strong className="text-white">South East Collectibles</strong> cleans,
            grades and consigns your grails — without the international hassle.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1, duration: 0.8 }}
            className="flex flex-wrap items-center gap-4"
          >
            <button
              onClick={() => scrollTo("studios")}
              className="rounded-full border border-white/25 hover:border-gold hover:text-gold text-white font-mono text-xs uppercase tracking-[0.15em] px-6 py-3.5 transition-colors duration-300"
              data-testid="hero-studios-btn"
            >
              Explore Studios
            </button>
            <button
              onClick={() => scrollTo("collectibles")}
              className="group inline-flex items-center gap-2 rounded-full bg-gold text-black font-mono text-xs uppercase tracking-[0.15em] px-6 py-3.5 font-semibold hover:bg-[#d59e19] transition-colors duration-300"
              data-testid="hero-collectibles-btn"
            >
              Card Services
              <ArrowDownRight
                size={16}
                className="group-hover:translate-y-0.5 group-hover:translate-x-0.5 transition-transform duration-300"
              />
            </button>
          </motion.div>
        </div>
      </motion.div>
    </section>
  );
}
